import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonPage, IonTitle, IonToolbar } from '@ionic/react';
import { addOutline } from 'ionicons/icons';
import { useRef, useState } from 'react';
import ModalFooter from '../components/ModalFooter';
import footerStyles from '../components/ModalFooter.module.css';
import RemoveIconButton from '../components/RemoveIconButton';
import { useCommonTranslation, useModalTranslation } from '../hooks/useTranslation';
import styles from './AddMicrochipModal.module.css';

export interface ManualMicrochipEntry {
    name: string;
    rfidCode: string;
}

interface FormEntry extends ManualMicrochipEntry {
    id: number;
}

/**
 * A microchip code as devices report them: the 15-digit decimal number of an
 * FDX-B chip, or the 10-character hex id of older tag formats.
 */
export function isValidRfidCode(value: string): boolean {
    return /^\d{15}$/.test(value) || /^[0-9a-fA-F]{10}$/.test(value);
}

/**
 * Normalise a manually entered code to how devices report them: hex ids
 * arrive lower case, so profiles must store them lower case to match
 * sightings. Decimal FDX-B numbers pass through unchanged.
 */
export function normalizeRfidCode(value: string): string {
    const trimmed = value.trim();
    return /^[0-9a-fA-F]{10}$/.test(trimmed) ? trimmed.toLowerCase() : trimmed;
}

function AddMicrochipModal({ dismiss, existingRfidCodes }: {
    dismiss: (entries?: ManualMicrochipEntry[] | null, role?: string) => void,
    existingRfidCodes: string[]
}) {
    const { t } = useModalTranslation();
    const { t: tCommon } = useCommonTranslation();
    const nextEntryId = useRef(1);
    const [entries, setEntries] = useState<FormEntry[]>([{ id: 0, name: '', rfidCode: '' }]);

    const updateEntry = (id: number, changes: Partial<ManualMicrochipEntry>) => {
        setEntries((current) => current.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)));
    };

    const addEntry = () => {
        setEntries((current) => [...current, { id: nextEntryId.current++, name: '', rfidCode: '' }]);
    };

    const removeEntry = (id: number) => {
        setEntries((current) => current.filter((entry) => entry.id !== id));
    };

    // The error to show under an entry's code input, if any. Codes already
    // saved as profiles or entered in an earlier row count as duplicates.
    const getCodeError = (entry: FormEntry, index: number) => {
        const rfidCode = entry.rfidCode.trim();

        if (!rfidCode) {
            return undefined;
        }

        if (!isValidRfidCode(rfidCode)) {
            return t('add_microchip.invalid_code');
        }

        const normalized = normalizeRfidCode(rfidCode);
        const isDuplicate = existingRfidCodes.includes(normalized)
            || entries.slice(0, index).some((other) => normalizeRfidCode(other.rfidCode) === normalized);

        return isDuplicate ? t('add_microchip.duplicate_code') : undefined;
    };

    const allEntriesValid = entries.every((entry, index) => (
        entry.name.trim() && entry.rfidCode.trim() && !getCodeError(entry, index)
    ));

    const confirm = () => {
        if (!allEntriesValid) {
            return;
        }

        dismiss(
            entries.map(({ name, rfidCode }) => ({ name: name.trim(), rfidCode: normalizeRfidCode(rfidCode) })),
            'confirm'
        );
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="medium" onClick={() => dismiss(null, 'cancel')}>
                            {tCommon('buttons.cancel')}
                        </IonButton>
                    </IonButtons>
                    <IonTitle>{t('add_microchip.title')}</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                <div className={styles.wrapper}>
                    <p className={styles.note}>{t('add_microchip.intro')}</p>
                    {entries.map((entry, index) => (
                        <IonList key={entry.id} className={`${styles.listCard} ${styles.entryCard}`}>
                            <IonItem>
                                <IonInput
                                    labelPlacement="stacked"
                                    placeholder={t('add_microchip.name_placeholder')}
                                    value={entry.name}
                                    maxlength={32}
                                    onIonInput={(event) => updateEntry(entry.id, { name: event.detail.value || '' })}
                                >
                                    <div slot="label" className={styles.fieldLabel}>{t('add_microchip.name_label')}</div>
                                </IonInput>
                                {entries.length > 1 && (
                                    <RemoveIconButton
                                        slot="end"
                                        ariaLabel={t('add_microchip.remove_entry')}
                                        onClick={() => removeEntry(entry.id)}
                                    />
                                )}
                            </IonItem>
                            <IonItem lines="none">
                                <IonInput
                                    labelPlacement="stacked"
                                    placeholder={t('add_microchip.code_placeholder')}
                                    helperText={t('add_microchip.code_hint')}
                                    errorText={getCodeError(entry, index)}
                                    className={`${styles.codeInput} ${getCodeError(entry, index) ? 'ion-invalid ion-touched' : ''}`}
                                    value={entry.rfidCode}
                                    maxlength={15}
                                    autocapitalize="off"
                                    spellcheck={false}
                                    onIonInput={(event) => updateEntry(entry.id, { rfidCode: event.detail.value || '' })}
                                >
                                    <div slot="label" className={styles.fieldLabel}>{t('add_microchip.code_label')}</div>
                                </IonInput>
                            </IonItem>
                        </IonList>
                    ))}
                    <IonList className={`${styles.listCard} ${styles.entryCard}`}>
                        <IonItem button detail={false} lines="none" onClick={addEntry} disabled={!allEntriesValid}>
                            <IonIcon slot="start" icon={addOutline} color="primary" className={styles.addRowIcon} />
                            <IonLabel color="primary" className={styles.addRowLabel}>
                                {t('add_microchip.add_another')}
                            </IonLabel>
                        </IonItem>
                    </IonList>
                </div>
            </IonContent>
            <ModalFooter>
                <IonButton
                    expand="block"
                    className={footerStyles.primaryButton}
                    onClick={confirm}
                    disabled={!allEntriesValid}
                >
                    {t('add_microchip.submit')}
                </IonButton>
            </ModalFooter>
        </IonPage>
    );
}

export default AddMicrochipModal;
